import { Fragment, useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { CheckCircle, Plus, Landmark, ChevronLeft, ChevronRight, ShieldAlert, Loader2 } from 'lucide-react'
import { Alert } from '@/components/ui/Alert'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal, DataTable, Pagination, Select } from '@/components/ui'
import { SearchableDropdown } from '@/components/ui/SearchableDropdown'
import { Tabs } from '@/components/ui/Tabs'
import { Input } from '@/components/ui/Input'
import { SortableTh } from '@/components/ui/SortableTh'
import { Lightbox } from '@/components/ui/Lightbox'
import { ImageWithFallback } from '@/components/ui/ImageWithFallback'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import type { Column } from '@/components/ui/DataTable'
import { useListSort } from '@/hooks/useListSort'
import {
  useBankTransactionsByMonth,
  useBankStatements,
  useMatchTransaction,
  useUnmatchTransaction,
  useCandidatePayments,
  useCandidateExpenses,
  useSuggestedCustomerId,
  useIgnoreTransaction,
  useUnignoreTransaction,
} from '@/hooks/useBankReconciliation'
import { usePayments, useReconCandidates, useFlagFraud, paymentKeys } from '@/hooks/usePayments'
import { useDateFormatter, useDateTimeFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { formatMonthYear, type CalendarSystem } from '@/lib/date'
import { useAuth } from '@/hooks/useAuth'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useQueryClient } from '@tanstack/react-query'
import type { BankTransaction, BankStatement, MlMatch } from '@/types'
import type { BadgeProps } from '@/components/ui/Badge'
import { UploadStatementModal } from './UploadStatementModal'
import { UploadStripePayoutModal } from './UploadStripePayoutModal'
import { BankStatementTieOutCard } from './BankStatementTieOutCard'
import { api } from '@/lib/api'
import type { CandidateExpense } from '@/hooks/useBankReconciliation'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const EMPTY_TRANSACTIONS: BankTransaction[] = []

/**
 * EP-145 A5: the Slip recon tab's own reading of `ml_match.outcome`.
 *
 * Deliberately NOT the shared ML_MATCH_CHIP map, because this tab means
 * something different: it lists slips that were VERIFIED but never matched to a
 * bank credit, so "no match" is the alarming case here and reads as an error,
 * where in the payments list it is merely neutral. The three legacy variants
 * below are unchanged from before; the four EP-143 outcomes were falling
 * through to `error` with no label at all.
 */
// HISTORICAL entries (ref1_exact, amount_trans_ref, ml_solo_verify) are still
// mapped: nothing writes them on a new settlement, but stored JSON carries them.
// See the outcome union in src/types/index.ts.
const RECON_ML_OUTCOME_VARIANT: Record<MlMatch['outcome'], BadgeProps['variant']> = {
  ref1_exact: 'success',
  amount_trans_ref: 'warning',
  ml_solo_verify: 'info',
  none: 'error',
  ml_agree: 'success',
  ml_disagree: 'warning',
  ml_pass_rdcw_fail: 'secondary',
}

function localDateString(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** How many months back from the current one the month list offers. The
    arrows beside it still step to any month outside it. */
const MONTH_OPTION_COUNT = 36

function buildMonthOptions(selected: string, current: string, locale: string, calendar: CalendarSystem) {
  const months = Array.from({ length: MONTH_OPTION_COUNT }, (_, i) => addMonths(current, -i))
  if (!months.includes(selected)) months.push(selected)
  months.sort().reverse()
  return months.map((value) => {
    const [y, m] = value.split('-').map(Number)
    return { value, label: formatMonthYear(new Date(y, m - 1, 1), { locale, calendar }) }
  })
}

function addMonths(month: string, delta: number) {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function formatCurrency(amount: number) {
  return formatBaht(amount, { decimals: 2, withSymbol: false })
}

// Format a statement period as "<start> \u2013 <end>" using the account-aware
// formatter (layout / timezone / Buddhist era), so periods honour Settings
// instead of the browser locale (B-020).
function formatDateRange(
  startDate: string,
  endDate: string,
  formatDate: (d: string | null | undefined) => string,
) {
  return `${formatDate(startDate)} \u2013 ${formatDate(endDate)}`
}

function matchRateBadge(rate: number) {
  if (rate >= 90) return <Badge variant="success">{rate.toFixed(1)}%</Badge>
  if (rate >= 50) return <Badge variant="warning">{rate.toFixed(1)}%</Badge>
  return <Badge variant="error">{rate.toFixed(1)}%</Badge>
}

function matchStatusBadge(status: string, t: (k: string) => string) {
  const variants: Record<string, 'success' | 'warning' | 'error' | 'info' | 'ghost'> = {
    auto_matched: 'success',
    manual_matched: 'info',
    unmatched: 'ghost',
    needs_review: 'warning',
    // T131-016: terminal, operator-set-aside — reads as neutral, not an error.
    ignored: 'ghost',
  }
  return (
    <Badge variant={variants[status] ?? 'ghost'}>{t(`match_status.${status}`)}</Badge>
  )
}

// ─── Manual Match Modal ───────────────────────────────────────────────────────

interface ManualMatchModalProps {
  transaction: BankTransaction
  onClose: () => void
  onMatch: (paymentId: number) => void
  onMatchExpense: (expenseId: number) => void
  isMatching: boolean
  // T131-012: threaded down to PaymentPicker's "Record payment" CTA so its
  // return-to-row link can restore the same month view.
  month: string
}

// ─── Expense option label helper ────────────────────────────────────────────

function expenseOptionLabel(exp: CandidateExpense, formatDate: (d: string | null | undefined) => string): string {
  const parts: string[] = [formatDate(exp.expense_date), `฿${formatCurrency(exp.net_cash_amount)}`]
  parts.push(exp.vendor_name || exp.category)
  return parts.join(' · ')
}

// Muted second line: the context that disambiguates same-amount expenses
// (category when the label already shows the vendor, property, free-text
// description). `localizeCategory` follows ExpenseListPage's pattern —
// expense.category is operator-configurable, not a fixed backend enum, so a
// custom category renders as-is rather than as a missing i18n key.
function expenseOptionDescription(
  exp: CandidateExpense,
  localizeCategory: (category: string) => string,
): string {
  const parts: string[] = []
  if (exp.vendor_name) parts.push(localizeCategory(exp.category))
  if (exp.property_name) parts.push(exp.property_name)
  if (exp.description) parts.push(exp.description)
  return parts.join(' · ')
}

// ─── Expense Picker (debit rows) ─────────────────────────────────────────────

interface ExpensePickerProps {
  transaction: BankTransaction
  onMatch: (expenseId: number) => void
  onClose: () => void
  isMatching: boolean
}

function ExpensePicker({ transaction, onMatch, onClose, isMatching }: ExpensePickerProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const { t: te, i18n } = useTranslation('expenses')
  const formatDate = useDateFormatter()

  const localizeCategory = useCallback(
    (category: string) =>
      i18n.exists(`expenses:categories.${category}`) ? te(`categories.${category}`) : category,
    [i18n, te],
  )

  // Initial candidate expenses from the direction-aware candidates endpoint
  const { data: candidateExpenses = [], isLoading: isCandidatesLoading } = useCandidateExpenses(transaction.id)

  // Expense search state — seeded from candidates, replaced with search results when user types
  const [expenseOptions, setExpenseOptions] = useState<CandidateExpense[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  // Seed options from candidates once they arrive (skip if the user has already searched)
  useEffect(() => {
    if (candidateExpenses.length > 0 && !hasSearched) {
      setExpenseOptions(candidateExpenses)
    }
  // hasSearched intentionally excluded — we only want to run this when candidates arrive
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateExpenses])

  // Pre-populate the selected value from the existing matched_expense (EP-116 saved-selection rule)
  const initialValue = transaction.matchable_type === 'Expense' && transaction.matchable_id != null
    ? String(transaction.matchable_id)
    : ''
  const [selectedExpenseId, setSelectedExpenseId] = useState<string>(initialValue)

  // When the pre-selected expense is not in the current options list, prepend it from matched_expense
  // so the label renders correctly when the modal re-opens on an already-matched debit row.
  const optionsWithPreselected: CandidateExpense[] =
    transaction.matched_expense &&
    transaction.matchable_type === 'Expense' &&
    transaction.matchable_id != null &&
    !expenseOptions.some((e) => e.id === transaction.matchable_id)
      ? [
          {
            id: transaction.matched_expense.id,
            expense_date: transaction.matched_expense.expense_date,
            total_amount: transaction.matched_expense.total_amount,
            net_cash_amount: transaction.matched_expense.net_cash_amount ?? transaction.matched_expense.total_amount,
            category: transaction.matched_expense.category,
            vendor_name: transaction.matched_expense.vendor_name ?? null,
            description: transaction.matched_expense.description ?? null,
            property_name: transaction.matched_expense.property_name ?? null,
          },
          ...expenseOptions,
        ]
      : expenseOptions

  const dropdownOptions = optionsWithPreselected.map((exp) => ({
    value: String(exp.id),
    label: expenseOptionLabel(exp, formatDate),
    description: expenseOptionDescription(exp, localizeCategory) || undefined,
  }))

  const handleSearch = useCallback(async (q: string) => {
    setHasSearched(true)
    setIsSearching(true)
    try {
      const { data } = await api.get<{ data: CandidateExpense[] }>('/api/expenses', { params: { q } })
      setExpenseOptions(data.data)
    } catch {
      // non-fatal: keep existing options
    } finally {
      setIsSearching(false)
    }
  }, [])

  const handleConfirm = () => {
    const id = parseInt(selectedExpenseId, 10)
    if (!isNaN(id)) onMatch(id)
  }

  return (
    <div className="space-y-4">
      {isCandidatesLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}
        </div>
      ) : (
        <SearchableDropdown
          options={dropdownOptions}
          value={selectedExpenseId}
          onChange={setSelectedExpenseId}
          placeholder={t('actions.search_expenses')}
          onSearch={handleSearch}
          isLoading={isSearching}
          label={t('labels.expense')}
        />
      )}

      {/* Nothing to match against: a debit with no expense on file usually means
          the expense simply has not been entered yet, so offer to create it
          rather than leaving the operator at a dead end. The bank row's amount
          and date carry over, and the reconciliation is picked up afterwards by
          re-opening this picker. */}
      {!isCandidatesLoading && dropdownOptions.length === 0 && (
        <div className="rounded-lg border border-dashed border-base-300 p-4 text-center">
          <p className="mb-3 text-sm text-base-content/60">{t('empty.no_expense_candidates')}</p>
          <Button
            as={Link}
            to={`/expenses/new?amount=${transaction.amount}&expense_date=${transaction.transaction_date.slice(0, 10)}`}
            variant="outline"
            leftIcon={<Plus className="h-4 w-4" />}
            data-testid="create-expense-from-bank-row"
          >
            {t('actions.create_expense')}
          </Button>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={onClose}>{tc('actions.cancel')}</Button>
        <Button
          onClick={handleConfirm}
          loading={isMatching}
          disabled={!selectedExpenseId}
        >
          {t('actions.select_expense')}
        </Button>
      </div>
    </div>
  )
}

// ─── Record Payment CTA link (T131-012) ──────────────────────────────────────
//
// Deep-links to the merged record-payment form (QuickPayFormPage, T149-088;
// formerly PaymentFormPage's create branch at /payments/new), consuming
// T131-010's prefill query params. `return_to` carries the bank row's
// transaction id + the currently viewed month so, after the payment is
// created, the form navigates back here instead of to /payments/:id, see
// the openTransactionId auto-open effect in TransactionTableByMonth.
function buildRecordPaymentUrl(
  transaction: BankTransaction,
  suggestedCustomerId: number | null,
  month: string,
): string {
  const params = new URLSearchParams()
  params.set('amount', String(transaction.amount))
  params.set('paid_at', transaction.transaction_date.slice(0, 10))
  if (suggestedCustomerId != null) params.set('customer_id', String(suggestedCustomerId))
  params.set('return_to', `/bank-reconciliation?open_transaction_id=${transaction.id}&month=${month}`)
  return `/payments/record?${params.toString()}`
}

// ─── Payment Picker (credit rows) ────────────────────────────────────────────

interface PaymentPickerProps {
  transaction: BankTransaction
  onMatch: (paymentId: number) => void
  isMatching: boolean
  month: string
}

function PaymentPicker({ transaction, onMatch, isMatching, month }: PaymentPickerProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tp } = useTranslation('payments')
  const formatDate = useDateFormatter()
  const [searchAmount, setSearchAmount] = useState(String(transaction.amount))
  const [searchRef, setSearchRef] = useState(transaction.reference ?? '')

  const txDate = new Date(transaction.transaction_date + 'T00:00:00')
  const fromDate = new Date(txDate)
  fromDate.setDate(fromDate.getDate() - 3)
  const toDate = new Date(txDate)
  toDate.setDate(toDate.getDate() + 4) // +4 so the API <= covers the full +3 day
  const [searchFrom, setSearchFrom] = useState(localDateString(fromDate))
  const [searchTo, setSearchTo] = useState(localDateString(toDate))

  const { data: paymentsData, isLoading } = usePayments(
    1,
    undefined,
    searchFrom || undefined,
    searchTo || undefined,
  )

  // T131-008/T131-009: the ranked /candidates endpoint (name_match per
  // candidate) + T131-011's suggested_customer_id sibling. Fetched alongside
  // the picker's own ad hoc date/amount search above so the badge — and
  // T131-012's Record Payment CTA — key off the authoritative candidate pool,
  // not the picker's local filter.
  const { data: rankedCandidates = [], isLoading: isCandidatesLoading } = useCandidatePayments(transaction.id)
  const { data: suggestedCustomerId = null } = useSuggestedCustomerId(transaction.id)
  const nameMatchById = new Map(rankedCandidates.map((c) => [c.id, c.name_match]))

  const candidates = (paymentsData?.data ?? []).filter((p) => {
    if (searchAmount && Math.abs(p.amount - parseFloat(searchAmount)) >= 0.01) return false
    if (searchRef && !(p.trans_ref ?? '').toLowerCase().includes(searchRef.toLowerCase())) return false
    return true
  })

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-2">
        <Input
          type="number"
          step="0.01"
          label={t('placeholders.search_amount')}
          size="sm"
          value={searchAmount}
          onChange={(e) => setSearchAmount(e.target.value)}
          aria-label={t('placeholders.search_amount')}
        />
        <Input
          type="date"
          label={t('placeholders.search_date_from')}
          size="sm"
          value={searchFrom}
          onChange={(e) => setSearchFrom(e.target.value)}
          aria-label={t('placeholders.search_date_from')}
        />
        <Input
          type="date"
          label={t('placeholders.search_date_to')}
          size="sm"
          value={searchTo}
          onChange={(e) => setSearchTo(e.target.value)}
          aria-label={t('placeholders.search_date_to')}
        />
        <Input
          type="text"
          label={t('placeholders.search_reference')}
          size="sm"
          value={searchRef}
          onChange={(e) => setSearchRef(e.target.value)}
          aria-label={t('placeholders.search_reference')}
        />
      </div>

      <div className="border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        ) : candidates.length === 0 ? (
          <div className="p-6 text-center text-sm text-base-content/60">
            {t('empty.payment_search')}
          </div>
        ) : (
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>{t('labels.paid_at')}</th>
                <th>{t('labels.amount')}</th>
                <th>{t('labels.payment_method')}</th>
                <th>{t('labels.trans_ref')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {candidates.map((payment) => (
                <tr key={payment.id}>
                  <td>{formatDate(payment.paid_at)}</td>
                  <td className="font-medium">
                    {formatCurrency(payment.amount)}
                    {nameMatchById.get(payment.id) && (
                      <Badge variant="success" size="xs" className="ml-2">
                        {t('badges.name_match')}
                      </Badge>
                    )}
                  </td>
                  <td>{tp(`methods.${payment.payment_method}`)}</td>
                  <td>{payment.trans_ref ?? '-'}</td>
                  <td>
                    <Button size="sm" onClick={() => onMatch(payment.id)} loading={isMatching}>
                      {t('actions.select_payment')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* T131-012: the ranked candidates endpoint came back empty — there is
          genuinely nothing to match against yet. Most credit rows land here
          (measured against production); offer to record the payment instead,
          mirroring ExpensePicker's create-expense CTA. Pre-selects the
          customer only when the payer name resolved to exactly one match. */}
      {!isCandidatesLoading && rankedCandidates.length === 0 && (
        <div className="rounded-lg border border-dashed border-base-300 p-4 text-center">
          <p className="mb-3 text-sm text-base-content/60">{t('empty.no_payment_candidates')}</p>
          <Button
            as={Link}
            to={buildRecordPaymentUrl(transaction, suggestedCustomerId, month)}
            variant="outline"
            leftIcon={<Plus className="h-4 w-4" />}
            data-testid="record-payment-from-bank-row"
          >
            {t('actions.record_payment')}
          </Button>
        </div>
      )}
    </div>
  )
}

function ManualMatchModal({ transaction, onClose, onMatch, onMatchExpense, isMatching, month }: ManualMatchModalProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()
  const isDebit = transaction.direction === 'debit'

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isDebit ? t('actions.search_expenses') : t('actions.search_payments')}
      size="3xl"
    >
      <div className="space-y-4">
        <div className="bg-base-200 rounded-lg p-3 text-sm space-y-1">
          <div className="flex gap-2">
            <span className="text-base-content/60 w-24">{t('labels.amount')}:</span>
            <span className="font-medium">{formatCurrency(transaction.amount)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-base-content/60 w-24">{t('labels.transaction_date')}:</span>
            <span>
              {formatDate(transaction.transaction_date)}
              {transaction.transaction_time && (
                <span className="ml-2 text-base-content/60">{transaction.transaction_time}</span>
              )}
            </span>
          </div>
          {transaction.reference && (
            <div className="flex gap-2">
              <span className="text-base-content/60 w-24">{t('labels.reference')}:</span>
              <span>{transaction.reference}</span>
            </div>
          )}
        </div>

        {isDebit ? (
          <ExpensePicker
            transaction={transaction}
            onMatch={onMatchExpense}
            onClose={onClose}
            isMatching={isMatching}
          />
        ) : (
          <>
            <PaymentPicker
              transaction={transaction}
              onMatch={onMatch}
              isMatching={isMatching}
              month={month}
            />
            <div className="flex justify-end">
              <Button variant="ghost" onClick={onClose}>{tc('actions.cancel')}</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

// ─── Review Candidates Row (inline expandable) ──────────────────────────────

interface ReviewCandidatesRowProps {
  transaction: BankTransaction
  colSpan: number
  onMatch: (paymentId: number) => void
  isMatching: boolean
}

function ReviewCandidatesRow({ transaction, colSpan, onMatch, isMatching }: ReviewCandidatesRowProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tp } = useTranslation('payments')
  const formatDate = useDateFormatter()

  const { data: candidates = [], isLoading } = useCandidatePayments(transaction.id)

  // Slip images for the lightbox. Payments store either a full URL or a bare
  // storage key (parking slips), so resolve the same way the payment detail
  // page does before handing them over.
  const [slipImages, setSlipImages] = useState<string[] | null>(null)
  const [slipIndex, setSlipIndex] = useState(0)
  const apiBase = import.meta.env.VITE_API_URL || ''
  const resolveSlipUrl = (raw: string) =>
    raw.startsWith('http') ? raw : `${apiBase}/api/images/${raw}`
  const openSlips = (images: string[]) => { setSlipIndex(0); setSlipImages(images) }

  // T131-014: the single-candidate/name-mismatch case (T131-013's matcher
  // change lands a row here specifically because its ONE same-amount
  // candidate's name disagrees) reads distinctly from a genuine
  // multiple-candidates row — a candidate awaiting confirmation, not a
  // failure or "0 matches".
  const isSingleMismatch = candidates.length === 1 && candidates[0].name_match === false

  return (
    <tr>
      <td colSpan={colSpan} className="bg-warning/5 p-0">
        <div className="px-6 py-3">
          <p className="text-xs font-medium text-warning mb-2">
            {isSingleMismatch
              ? t('candidates_count_single_mismatch')
              : t('candidates_count', { count: candidates.length })}
          </p>
          {isLoading ? (
            <Skeleton className="h-10" />
          ) : candidates.length === 0 ? (
            <p className="text-sm text-base-content/60">{t('empty.payment_search')}</p>
          ) : (
            <table className="table table-sm w-full">
              <thead>
                <tr className="text-xs">
                  <th>{t('labels.paid_at')}</th>
                  <th>{t('labels.slip')}</th>
                  <th>{t('labels.amount')}</th>
                  <th>{t('labels.customer')}</th>
                  <th>{t('labels.invoice')}</th>
                  <th>{t('labels.payment_method')}</th>
                  <th>{t('labels.trans_ref')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.paid_at)}</td>
                    {/* The slip carries the payer, amount and timestamp to check
                        against the bank row — the whole reason an operator opens
                        a candidate. Shown inline so that check needs no
                        navigation; click enlarges. */}
                    <td>
                      {payment.slip_images?.length ? (
                        <button
                          type="button"
                          className="block"
                          onClick={(e) => {
                            e.stopPropagation()
                            openSlips(payment.slip_images.map(resolveSlipUrl))
                          }}
                          aria-label={t('labels.view_slip')}
                        >
                          <ImageWithFallback
                            src={resolveSlipUrl(payment.slip_images[0])}
                            alt={t('labels.slip')}
                            className="h-10 w-10 rounded object-cover border border-base-300 hover:opacity-80"
                          />
                        </button>
                      ) : (
                        <span className="text-xs text-base-content/40">—</span>
                      )}
                    </td>
                    <td className="font-medium">
                      {formatCurrency(payment.amount)}
                      {payment.name_match && (
                        <Badge variant="success" size="xs" className="ml-2">
                          {t('badges.name_match')}
                        </Badge>
                      )}
                      {/* T131-014: the ONLY candidate, and its name disagrees — a
                          positive visual flag, not just the absence of a match
                          badge, since there is nothing else to compare it against. */}
                      {isSingleMismatch && (
                        <Badge variant="warning" size="xs" className="ml-2">
                          {t('badges.name_mismatch')}
                        </Badge>
                      )}
                    </td>
                    <td>{payment.customer_name ?? '-'}</td>
                    <td>
                      {payment.invoice_id ? (
                        <Link
                          to={`/invoices/${payment.invoice_id}`}
                          target="_blank"
                          className="link link-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {payment.invoice_number ?? `#${payment.invoice_id}`}
                        </Link>
                      ) : (
                        /* A payment with no invoice is normal here — the record-payment
                           CTA creates one — so show the payment itself instead. */
                        <Link
                          to={`/payments/${payment.id}`}
                          target="_blank"
                          className="link link-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('labels.view_payment')}
                        </Link>
                      )}
                    </td>
                    <td>{tp(`methods.${payment.payment_method}`)}</td>
                    <td>{payment.trans_ref ?? '-'}</td>
                    <td>
                      <Button size="sm" onClick={() => onMatch(payment.id)} loading={isMatching}>
                        {t('actions.select_payment')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {slipImages && (
            <Lightbox
              images={slipImages}
              index={slipIndex}
              onIndexChange={setSlipIndex}
              onClose={() => setSlipImages(null)}
            />
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Unmatch Confirm Modal ────────────────────────────────────────────────────

interface UnmatchConfirmModalProps {
  transaction: BankTransaction
  onClose: () => void
  onConfirm: () => void
  isUnmatching: boolean
}

function UnmatchConfirmModal({ transaction, onClose, onConfirm, isUnmatching }: UnmatchConfirmModalProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()

  return (
    <Modal isOpen onClose={onClose} title={t('actions.confirm_unmatch')}>
      <div className="space-y-4">
        <p>{t('messages.unmatch_confirm')}</p>
        <div className="bg-base-200 rounded-lg p-3 text-sm space-y-1">
          <div className="flex gap-2">
            <span className="text-base-content/60 w-24">{t('labels.amount')}:</span>
            <span className="font-medium">{formatCurrency(transaction.amount)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-base-content/60 w-24">{t('labels.transaction_date')}:</span>
            <span>
              {formatDate(transaction.transaction_date)}
              {transaction.transaction_time && (
                <span className="ml-2 text-base-content/60">{transaction.transaction_time}</span>
              )}
            </span>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>{tc('actions.cancel')}</Button>
          <Button variant="error" onClick={onConfirm} loading={isUnmatching}>
            {t('actions.unmatch')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Ignore Confirm Modal (T131-016) ──────────────────────────────────────────

interface IgnoreConfirmModalProps {
  transaction: BankTransaction
  onClose: () => void
  onConfirm: () => void
  isIgnoring: boolean
}

function IgnoreConfirmModal({ transaction, onClose, onConfirm, isIgnoring }: IgnoreConfirmModalProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()

  return (
    <Modal isOpen onClose={onClose} title={t('actions.confirm_ignore')}>
      <div className="space-y-4">
        <p>{t('messages.ignore_confirm')}</p>
        <div className="bg-base-200 rounded-lg p-3 text-sm space-y-1">
          <div className="flex gap-2">
            <span className="text-base-content/60 w-24">{t('labels.amount')}:</span>
            <span className="font-medium">{formatCurrency(transaction.amount)}</span>
          </div>
          <div className="flex gap-2">
            <span className="text-base-content/60 w-24">{t('labels.transaction_date')}:</span>
            <span>
              {formatDate(transaction.transaction_date)}
              {transaction.transaction_time && (
                <span className="ml-2 text-base-content/60">{transaction.transaction_time}</span>
              )}
            </span>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose}>{tc('actions.cancel')}</Button>
          <Button variant="error" onClick={onConfirm} loading={isIgnoring}>
            {t('actions.ignore')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Transaction Table ────────────────────────────────────────────────────────

// EP-97: `needs_attention` (default) = the reconciliation work queue (unmatched +
// needs_review); the BE resolves it via `match_status=unresolved`.
// The two statuses that count as reconciled. Used to decide whether a row
// offers "unmatch" — NOT for filtering the list, which is server-side (a
// client-side filter sees only the page it was handed).
const MATCHED_STATUSES = ['auto_matched', 'manual_matched']

// T131-016: 'ignored' is a terminal, operator-set-aside status with its own tab.
type MatchStatusFilter = 'needs_attention' | 'all' | 'matched' | 'needs_review' | 'unmatched' | 'ignored'

interface TransactionTableByMonthProps {
  month: string
  subTab: MatchStatusFilter
  page: number
  onPageChange: (p: number) => void
  // T131-012: bank row id to auto-reopen the ManualMatchModal for, once its
  // month/tab data loads — the "return-to-row" behavior after the Record
  // Payment CTA's create-payment round trip through PaymentFormPage.
  openTransactionId?: number | null
}

function TransactionTableByMonth({ month, subTab, page, onPageChange, openTransactionId }: TransactionTableByMonthProps) {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()

  const [matchingTx, setMatchingTx] = useState<BankTransaction | null>(null)
  const [unmatchingTx, setUnmatchingTx] = useState<BankTransaction | null>(null)
  const [ignoringTx, setIgnoringTx] = useState<BankTransaction | null>(null)
  const [expandedReviewId, setExpandedReviewId] = useState<number | null>(null)

  // EP-97: server-side sort — transaction_date desc default (imported rows never
  // change), amount the only other reconciler axis.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'transaction_date',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { onPageChange(1); setSort(key) }
  const handleToggleOrder = () => { onPageChange(1); toggleOrder() }

  // EP-97: sub-tab → API match_status. The BE default (blank) returns only the
  // unresolved work queue, so `all` MUST send an explicit `all` to see every
  // status. `needs_attention` maps to the BE's `unresolved` set (unmatched +
  // needs_review), and `matched` to its auto+manual pair — both resolved
  // server-side, so paging and the tab badge count the same rows.
  const apiMatchStatus =
    subTab === 'needs_attention' ? 'unresolved' :
    subTab === 'all' ? 'all' :
    subTab

  const { data, isLoading } = useBankTransactionsByMonth(month, apiMatchStatus, page, sortParams)

  // EP-97: mobile sort options mirror the allow-listed sort keys.
  const sortOptions = [
    { value: 'transaction_date', label: tc('sort.by_transaction_date') },
    { value: 'amount', label: tc('sort.by_amount') },
  ]

  const matchMutation = useMatchTransaction()
  const unmatchMutation = useUnmatchTransaction()
  const ignoreMutation = useIgnoreTransaction()
  const unignoreMutation = useUnignoreTransaction()
  const [unignoringId, setUnignoringId] = useState<number | null>(null)

  // Filtering is entirely server-side. It must be: a client-side filter can only
  // ever see the page it was handed, so the "matched" tab showed whichever
  // matched rows happened to fall on page 1 while its badge — read from the
  // server-side summary — counted them all, and the pager sized itself to the
  // unfiltered set.
  const allTransactions = data?.data ?? EMPTY_TRANSACTIONS
  const transactions = allTransactions

  // T131-012: "return to row" — once the data containing the transaction the
  // operator was recording a payment for has loaded, reopen its match modal
  // automatically. Guarded to fire once (not on every subsequent refetch, e.g.
  // after the operator manually closes the modal or matches the row).
  const hasAutoOpenedRef = useRef(false)
  useEffect(() => {
    if (!openTransactionId || hasAutoOpenedRef.current) return
    const target = allTransactions.find((tx) => tx.id === openTransactionId)
    if (target) {
      hasAutoOpenedRef.current = true
      setMatchingTx(target)
    }
  }, [openTransactionId, allTransactions])

  const handleMatch = async (paymentId: number) => {
    if (!matchingTx) return
    await matchMutation.mutateAsync({ transactionId: matchingTx.id, matchableType: 'Payment', matchableId: paymentId })
    setMatchingTx(null)
  }

  const handleMatchExpense = async (expenseId: number) => {
    if (!matchingTx) return
    await matchMutation.mutateAsync({ transactionId: matchingTx.id, matchableType: 'Expense', matchableId: expenseId })
    setMatchingTx(null)
  }

  const handleInlineMatch = async (transactionId: number, paymentId: number) => {
    await matchMutation.mutateAsync({ transactionId, matchableType: 'Payment', matchableId: paymentId })
    setExpandedReviewId(null)
  }

  const handleUnmatch = async () => {
    if (!unmatchingTx) return
    await unmatchMutation.mutateAsync(unmatchingTx.id)
    setUnmatchingTx(null)
  }

  const handleIgnore = async () => {
    if (!ignoringTx) return
    await ignoreMutation.mutateAsync(ignoringTx.id)
    setIgnoringTx(null)
  }

  const handleUnignore = async (transactionId: number) => {
    setUnignoringId(transactionId)
    try {
      await unignoreMutation.mutateAsync(transactionId)
    } finally {
      setUnignoringId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    )
  }

  if (!transactions.length) {
    return <EmptyState title={t('empty.transactions')} />
  }

  const isMatchedTab = subTab === 'matched'

  return (
    <>
      {/* EP-97: mobile column sort (desktop uses the sortable table headers). */}
      <div className="w-full sm:hidden mb-3">
        <MobileSortControl
          options={sortOptions}
          value={sort}
          order={order}
          onChange={handleSort}
          onToggleOrder={handleToggleOrder}
        />
      </div>
      <div className="overflow-x-auto">
        <table className="table w-full">
          <thead>
            <tr>
              <SortableTh sortKey="transaction_date" sortBy={sort} sortOrder={order} onSort={handleSort}>
                {t('labels.transaction_date')}
              </SortableTh>
              <SortableTh sortKey="amount" sortBy={sort} sortOrder={order} onSort={handleSort}>
                {t('labels.amount')}
              </SortableTh>
              <th>{t('labels.direction_credit') + '/' + t('labels.direction_debit')}</th>
              <th>{t('labels.reference')}</th>
              <th>{t('labels.description')}</th>
              <th>{t('labels.match_status')}</th>
              {isMatchedTab && <th>{t('labels.matched_payment')}</th>}
              <th>{tc('labels.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => {
              const isReviewExpanded = expandedReviewId === tx.id
              const colSpan = isMatchedTab ? 8 : 7

              return (
                <Fragment key={tx.id}>
                  <tr className={tx.match_status === 'needs_review' ? 'cursor-pointer hover' : ''} onClick={tx.match_status === 'needs_review' ? () => setExpandedReviewId(isReviewExpanded ? null : tx.id) : undefined}>
                    <td>
                      {formatDate(tx.transaction_date)}
                      {/* The bank's clock time — what the operator compares against
                          the payer's slip. Absent on exports that carry none. */}
                      {tx.transaction_time && (
                        <span className="block text-xs text-base-content/60">{tx.transaction_time}</span>
                      )}
                    </td>
                    <td className="font-medium">{formatCurrency(tx.amount)}</td>
                    <td>
                      {tx.direction === 'debit' ? (
                        <Badge variant="error">{t('labels.direction_debit')}</Badge>
                      ) : (
                        <Badge variant="success">{t('labels.direction_credit')}</Badge>
                      )}
                    </td>
                    <td>{tx.reference || '-'}</td>
                    <td className="max-w-xs truncate">{tx.description || '-'}</td>
                    <td>{matchStatusBadge(tx.match_status, t)}</td>
                    {isMatchedTab && (
                      <td>
                        {tx.matched_expense ? (
                          <span className="text-sm">
                            <span className="text-base-content/60">{formatDate(tx.matched_expense.expense_date)}</span>
                            {' · '}
                            <span>{tx.matched_expense.category}</span>
                            {tx.matched_expense.vendor_name && (
                              <>
                                {' · '}
                                <span className="text-base-content/60">{tx.matched_expense.vendor_name}</span>
                              </>
                            )}
                          </span>
                        ) : tx.matched_payment ? (
                          <span className="text-sm">
                            {formatCurrency(tx.matched_payment.amount)}{' '}
                            <span className="text-base-content/60">
                              {formatDate(tx.matched_payment.paid_at)}
                            </span>
                          </span>
                        ) : '-'}
                      </td>
                    )}
                    <td>
                      <div className="flex gap-2">
                        {MATCHED_STATUSES.includes(tx.match_status) ? (
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setUnmatchingTx(tx) }}>
                            {t('actions.unmatch')}
                          </Button>
                        ) : tx.match_status === 'ignored' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleUnignore(tx.id) }}
                            loading={unignoringId === tx.id}
                            data-testid={`unignore-row-${tx.id}`}
                          >
                            {t('actions.unignore')}
                          </Button>
                        ) : (
                          <>
                            {tx.match_status === 'needs_review' ? (
                              <Button size="sm" variant="warning" onClick={(e) => { e.stopPropagation(); setExpandedReviewId(isReviewExpanded ? null : tx.id) }}>
                                {isReviewExpanded ? tc('actions.close') : t('actions.review')}
                              </Button>
                            ) : (
                              <Button size="sm" onClick={() => setMatchingTx(tx)}>
                                {t('actions.match')}
                              </Button>
                            )}
                            {/* T131-016: visible on unmatched + needs_review rows only. */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); setIgnoringTx(tx) }}
                              data-testid={`ignore-row-${tx.id}`}
                            >
                              {t('actions.ignore')}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isReviewExpanded && tx.match_status === 'needs_review' && (
                    <ReviewCandidatesRow
                      transaction={tx}
                      colSpan={colSpan}
                      onMatch={(paymentId) => handleInlineMatch(tx.id, paymentId)}
                      isMatching={matchMutation.isPending}
                    />
                  )}
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {data?.meta && data.meta.total_pages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            onPageChange={onPageChange}
            stickyOnMobile={false}
          />
        </div>
      )}

      {matchingTx && (
        <ManualMatchModal
          transaction={matchingTx}
          onClose={() => setMatchingTx(null)}
          onMatch={handleMatch}
          onMatchExpense={handleMatchExpense}
          isMatching={matchMutation.isPending}
          month={month}
        />
      )}

      {unmatchingTx && (
        <UnmatchConfirmModal
          transaction={unmatchingTx}
          onClose={() => setUnmatchingTx(null)}
          onConfirm={handleUnmatch}
          isUnmatching={unmatchMutation.isPending}
        />
      )}

      {ignoringTx && (
        <IgnoreConfirmModal
          transaction={ignoringTx}
          onClose={() => setIgnoringTx(null)}
          onConfirm={handleIgnore}
          isIgnoring={ignoreMutation.isPending}
        />
      )}
    </>
  )
}

// ─── Slip Recon Tab (EP-143 T143-016) ────────────────────────────────────────
//
// Lists payments that were auto/ML-verified but whose bank credit was NOT found
// in the imported statements after the 48h window (slip_metadata.recon_candidate).
// Admin+ only (enforced server-side — 403 for staff/accountant).

function SlipReconTab() {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()
  const formatDateTime = useDateTimeFormatter()
  const addToast = useNotificationStore((s) => s.addToast)
  const queryClient = useQueryClient()

  const { data: candidates = [], isLoading, isFetching } = useReconCandidates()
  const flagFraud = useFlagFraud()
  const [flaggingId, setFlaggingId] = useState<number | null>(null)

  const handleFlagFraud = async (id: number) => {
    setFlaggingId(id)
    try {
      await flagFraud.mutateAsync(id)
      // Invalidation already happens inside useFlagFraud.onSuccess, but force a
      // fresh fetch of the recon list so the flagged row disappears.
      queryClient.invalidateQueries({ queryKey: [...paymentKeys.all, 'recon_candidates'] })
      addToast({ type: 'success', message: t('slip_recon.flag_success') })
    } catch {
      // non-fatal; error toast handled by the global error boundary
    } finally {
      setFlaggingId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    )
  }

  if (!candidates.length) {
    return (
      <EmptyState
        icon={<ShieldAlert className="w-12 h-12" />}
        title={t('slip_recon.empty_title')}
        description={t('slip_recon.empty_description')}
      />
    )
  }

  return (
    <>
      {/* Refreshing indicator while a background refetch is in flight */}
      {isFetching && !isLoading && (
        <div className="flex items-center gap-2 mb-3 text-sm text-base-content/60">
          <Loader2 className="w-4 h-4 animate-spin text-base-content/40" aria-label={tc('labels.refreshing')} />
        </div>
      )}

      {/* Primary section: recon-candidate rows */}
      <div className="overflow-x-auto">
        <table className="table w-full" data-testid="recon-candidates-table">
          <thead>
            <tr>
              <th>{t('slip_recon.col_paid_at')}</th>
              <th>{t('slip_recon.col_amount')}</th>
              <th>{t('slip_recon.col_customer')}</th>
              <th>{t('slip_recon.col_slip_uploaded_at')}</th>
              <th>{t('slip_recon.col_gate_summary')}</th>
              <th>{tc('labels.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {candidates.map((c) => (
              <tr key={c.id} data-testid={`recon-row-${c.id}`}>
                <td>
                  <span className="font-medium">{formatDate(c.paid_at)}</span>
                </td>
                <td className="font-medium">{formatCurrency(c.amount)}</td>
                <td>
                  {c.customer ? (
                    <div className="min-w-0">
                      <span className="block truncate">{c.customer.name}</span>
                      {c.customer.phone && (
                        <span className="block text-xs text-base-content/60 truncate">{c.customer.phone}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-base-content/40">—</span>
                  )}
                </td>
                <td>
                  {c.slip_uploaded_at ? (
                    <span className="text-sm">{formatDateTime(c.slip_uploaded_at)}</span>
                  ) : (
                    <span className="text-base-content/40">—</span>
                  )}
                </td>
                <td>
                  {/* Gate summary: ml_match outcome chip */}
                  {c.ml_match ? (
                    <div className="flex flex-wrap gap-1 min-w-0">
                      <Badge variant={RECON_ML_OUTCOME_VARIANT[c.ml_match.outcome]} size="sm">
                        {t(`slip_recon.ml_outcome.${c.ml_match.outcome}`)}
                      </Badge>
                    </div>
                  ) : (
                    <span className="text-base-content/40">—</span>
                  )}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="error"
                    onClick={() => handleFlagFraud(c.id)}
                    loading={flaggingId === c.id}
                    data-testid={`flag-fraud-${c.id}`}
                  >
                    {t('slip_recon.flag_fraud')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Secondary section: link to the existing unmatched-credits lane */}
      <div className="mt-8 border-t border-base-200 pt-6">
        <h3 className="text-sm font-medium text-base-content/70 mb-2">
          {t('slip_recon.orphan_section_title')}
        </h3>
        <p className="text-sm text-base-content/60 mb-3">
          {t('slip_recon.orphan_section_description')}
        </p>
        {/* The orphan (unmatched credits) lane already lives on the Transactions
            tab under "Needs Attention" → switch there rather than rebuilding it. */}
        <Button
          variant="outline"
          size="sm"
          as={Link}
          to="/bank-reconciliation"
          onClick={() => {
            // Programmatic tab switch is not possible without lifting state;
            // navigating to the same page resets it to the default (Needs Attention).
          }}
        >
          {t('slip_recon.orphan_section_link')}
        </Button>
      </div>
    </>
  )
}

// ─── Statements Tab ───────────────────────────────────────────────────────────

function StatementsTab() {
  const { t } = useTranslation('bank_reconciliation')
  const { t: tc } = useTranslation('common')
  const formatDate = useDateFormatter()
  const [page, setPage] = useState(1)

  const { data, isLoading, error } = useBankStatements(page)
  const statements = data?.data ?? []

  const columns: Column<BankStatement>[] = [
    {
      key: 'bank_name',
      header: t('labels.bank_name'),
      render: (s) => <span className="font-medium">{t(`banks.${s.bank_name}`)}</span>,
    },
    {
      key: 'period',
      header: t('labels.period'),
      render: (s) => formatDateRange(s.start_date, s.end_date, formatDate),
    },
    {
      key: 'row_count',
      header: t('labels.row_count'),
      render: (s) => s.row_count,
    },
    {
      key: 'matched_count',
      header: t('labels.matched_count'),
      render: (s) => s.matched_count,
    },
    {
      key: 'match_rate',
      header: t('labels.match_rate'),
      render: (s) => matchRateBadge(s.match_rate),
    },
    {
      key: 'created_by_name',
      header: t('labels.uploaded_by'),
      render: (s) => s.created_by_name || '-',
    },
  ]

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <EmptyState
        title={t('messages.error_loading')}
        description={t('messages.error_description')}
        action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
      />
    )
  }

  if (!statements.length) {
    return (
      <EmptyState
        icon={<Landmark className="w-12 h-12" />}
        title={t('empty.statements')}
      />
    )
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={statements}
        keyExtractor={(s) => s.id}
      />
      {data?.meta && data.meta.total_pages > 1 && (
        <div className="flex justify-center mt-4">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            onPageChange={setPage}
            stickyOnMobile={false}
          />
        </div>
      )}
    </>
  )
}

// ─── Summary Cards ────────────────────────────────────────────────────────────

interface SummaryCardsProps {
  total: number
  matched: number
  needsReview: number
  unmatched: number
  creditMatchRatePct?: number
  debitMatchRatePct?: number
  isLoading: boolean
}

function SummaryCards({ total, matched, needsReview, unmatched, creditMatchRatePct, debitMatchRatePct, isLoading }: SummaryCardsProps) {
  const { t } = useTranslation('bank_reconciliation')

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-16 sm:h-20" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <Card className="text-center" bodyClassName="p-2 sm:p-4">
          <p className="text-xs text-base-content/60 mb-1">{t('summary.total')}</p>
          <p className="text-lg sm:text-2xl font-bold">{total}</p>
        </Card>
        <Card className="text-center" bodyClassName="p-2 sm:p-4">
          <p className="text-xs text-base-content/60 mb-1">{t('summary.matched')}</p>
          <p className="text-lg sm:text-2xl font-bold text-success">{matched}</p>
        </Card>
        <Card className="text-center" bodyClassName="p-2 sm:p-4">
          <p className="text-xs text-base-content/60 mb-1">{t('summary.needs_review')}</p>
          <p className="text-lg sm:text-2xl font-bold text-warning">{needsReview}</p>
        </Card>
        <Card className="text-center" bodyClassName="p-2 sm:p-4">
          <p className="text-xs text-base-content/60 mb-1">{t('summary.unmatched')}</p>
          <p className="text-lg sm:text-2xl font-bold text-error">{unmatched}</p>
        </Card>
      </div>
      {/* T128-012: direction-split match rates — show when the backend provides them */}
      {(creditMatchRatePct != null || debitMatchRatePct != null) && (
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          {creditMatchRatePct != null && (
            <Card className="text-center" bodyClassName="p-2 sm:p-3">
              <p className="text-xs text-base-content/60 mb-1">{t('summary.credit_match_rate')}</p>
              <div className="flex justify-center">{matchRateBadge(creditMatchRatePct)}</div>
            </Card>
          )}
          {debitMatchRatePct != null && (
            <Card className="text-center" bodyClassName="p-2 sm:p-3">
              <p className="text-xs text-base-content/60 mb-1">{t('summary.debit_match_rate')}</p>
              <div className="flex justify-center">{matchRateBadge(debitMatchRatePct)}</div>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

// EP-143 T143-016: 'slip_recon' tab is admin+ only; excluded from the Select
// options for staff and accountant (server also 403s if they somehow call it).
type OuterTab = 'transactions' | 'statements' | 'slip_recon'

export function BankReconciliationPage() {
  const { t } = useTranslation('bank_reconciliation')
  const { user } = useAuth()
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  const canUploadStatement = isAdminOrOwner || user?.role === 'accountant'
  const [searchParams, setSearchParams] = useSearchParams()

  // T131-012: "return to row" — the Record Payment CTA's return_to link lands
  // back here with ?open_transaction_id=&month= so the operator sees the same
  // bank row's match modal reopened instead of a bare list.
  const openTransactionIdParam = searchParams.get('open_transaction_id')
  const openTransactionId = openTransactionIdParam ? Number(openTransactionIdParam) : null
  const monthParam = searchParams.get('month')

  const defaultMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  const [month, setMonth] = useState(monthParam || defaultMonth)
  const { calendar } = useLocalizationConfig()
  const { i18n } = useTranslation()
  const monthOptions = buildMonthOptions(month, defaultMonth, i18n.language, calendar)
  const [outerTab, setOuterTab] = useState<OuterTab>('transactions')
  // EP-97: open on the reconciliation work queue (unmatched + needs_review).
  const [subTab, setSubTab] = useState<MatchStatusFilter>('needs_attention')
  const [txPage, setTxPage] = useState(1)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [stripeUploadOpen, setStripeUploadOpen] = useState(false)
  const uploadRequested = canUploadStatement && searchParams.get('upload') === '1'

  const closeUpload = () => {
    setUploadOpen(false)
    if (uploadRequested) {
      const next = new URLSearchParams(searchParams)
      next.delete('upload')
      setSearchParams(next, { replace: true })
    }
  }

  // Fetch summary for the selected month (no match status filter = all transactions)
  const { data: monthData, isLoading: isSummaryLoading, isFetching: isSummaryFetching, isError: isSummaryError } = useBankTransactionsByMonth(month)
  const summary = monthData?.summary

  // EP-128: the legacy top-level summary keys are credit-only (API compat),
  // but the lists show BOTH directions — display counts must combine the
  // credits/debits sub-hashes or the tab badges disagree with the rows
  // (caught in CiC verification). Fall back to legacy keys pre-split.
  const combined = summary?.credits && summary?.debits
    ? {
        matched: summary.credits.matched + summary.debits.matched,
        attention: summary.credits.unmatched + summary.debits.unmatched,
        needsReview: (summary.credits.needs_review ?? 0) + (summary.debits.needs_review ?? 0),
        // T131-016: ignored has its own count, not a subtraction from the others
        // (the backend already excludes ignored rows from matched/unmatched/needs_review).
        ignored: (summary.credits.ignored ?? 0) + (summary.debits.ignored ?? 0),
      }
    : {
        matched: summary?.matched ?? 0,
        attention: (summary?.unmatched ?? 0) + (summary?.needs_review ?? 0),
        needsReview: summary?.needs_review ?? 0,
        ignored: 0,
      }
  const combinedTotal = combined.matched + combined.attention
  const combinedUnmatched = combined.attention - combined.needsReview

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth)
    setTxPage(1)
  }

  const handleSubTabChange = (tab: MatchStatusFilter) => {
    setSubTab(tab)
    setTxPage(1)
  }

  const subTabs: { key: MatchStatusFilter; label: string; count?: number }[] = [
    {
      key: 'needs_attention',
      label: t('tabs.needs_attention'),
      count: combined.attention,
    },
    { key: 'all', label: t('tabs.all'), count: combinedTotal },
    { key: 'matched', label: t('reconciliation.tabs.matched'), count: combined.matched },
    { key: 'needs_review', label: t('reconciliation.tabs.needs_review'), count: combined.needsReview },
    { key: 'unmatched', label: t('reconciliation.tabs.unmatched'), count: combinedUnmatched },
    { key: 'ignored', label: t('reconciliation.tabs.ignored'), count: combined.ignored },
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('title') }]} />

      <PageHeader
        title={t('title')}
        description={t('description')}
        isRefreshing={isSummaryFetching && !isSummaryLoading}
        // Both header labels are long Thai strings; collapse the Stripe-import
        // overflow into the ตัวเลือก/Options sheet on mobile (rather than showing
        // it directly) so it doesn't crowd the "Upload statement" primary.
        collapseOverflow
        primaryAction={{
          key: 'new-statement',
          label: t('statements.new'),
          hidden: !canUploadStatement,
          icon: Plus,
          onClick: () => setUploadOpen(true),
        }}
        overflowActions={[
          {
            key: 'upload-stripe-payout',
            label: t('stripe_payout.upload_action'),
            icon: Plus,
            onClick: () => setStripeUploadOpen(true),
          },
        ]}
      />

      {/* Month filter. Always shown: it is the only filter, so a collapse
          toggle on mobile only hid it (owner, 2026-09-29). The month list is a
          kit Select, which opens an in-app sheet on a phone rather than the
          iOS month wheel that <input type="month"> brought up. */}
      <Card>
        <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3">
          <span id="bank-recon-month-label" className="w-full text-sm font-medium sm:w-auto">{t('labels.select_month')}</span>
          <Button
            variant="ghost"
            size="sm"
            shape="square"
            onClick={() => handleMonthChange(addMonths(month, -1))}
            aria-label={t('previous_month')}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Select
            aria-labelledby="bank-recon-month-label"
            sheetTitle={t('labels.select_month')}
            size="sm"
            className="min-w-0 flex-1 sm:w-48 sm:flex-none"
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
            options={monthOptions}
          />
          <Button
            variant="ghost"
            size="sm"
            shape="square"
            onClick={() => handleMonthChange(addMonths(month, 1))}
            aria-label={t('next_month')}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </Card>

      {isSummaryError && <Alert variant="error" role="alert">{t('messages.error_loading')}</Alert>}

      {/* Ready for Accounting Banner */}
      {!isSummaryLoading && summary?.ready_for_accounting && summary.total > 0 && (
        <Alert variant="success" icon={<CheckCircle className="w-5 h-5 shrink-0" />}>
          <span>{t('messages.ready_for_accounting')}</span>
        </Alert>
      )}

      {/* Summary Cards */}
      <SummaryCards
        total={combinedTotal}
        matched={combined.matched}
        needsReview={combined.needsReview}
        unmatched={combinedUnmatched}
        creditMatchRatePct={summary?.credits?.match_rate_pct}
        debitMatchRatePct={summary?.debits?.match_rate_pct}
        isLoading={isSummaryLoading}
      />

      {!isSummaryError && monthData?.tie_out && <BankStatementTieOutCard tieOut={monthData.tie_out} />}

      {/* View switcher (Transactions / Statements / Slip recon) — a dropdown (Select).
          The Slip recon option is only surfaced for admin+ (the BE also 403s for
          others, but hiding keeps the UI uncluttered for staff/accountant). */}
      <Card>
        <Select
          aria-label={t('tabs.view')}
          value={outerTab}
          onChange={(e) => setOuterTab(e.target.value as OuterTab)}
          size="sm"
          className="mb-6 w-full sm:w-72"
          options={[
            { value: 'transactions', label: t('tabs.transactions') },
            { value: 'statements', label: t('tabs.statements') },
            ...(isAdminOrOwner ? [{ value: 'slip_recon', label: t('tabs.slip_recon') }] : []),
          ]}
        />

        {outerTab === 'transactions' && (
          <div className="space-y-4">
            {/* Status filter — a horizontal tab strip on EVERY viewport (owner
                call: tabs on mobile too, not the Select fallback). It manages its
                own overflow via overflow-x-auto so the 5 status tabs scroll on a
                narrow screen; counts are folded into each tab label. */}
            <Tabs
              value={subTab}
              onChange={(v) => handleSubTabChange(v as MatchStatusFilter)}
              mobileSelect={false}
              className="mb-4 overflow-x-auto flex-nowrap"
              options={subTabs.map((tab) => ({
                value: tab.key,
                label: tab.count != null ? `${tab.label} (${tab.count})` : tab.label,
              }))}
            />

            <TransactionTableByMonth
              month={month}
              subTab={subTab}
              page={txPage}
              onPageChange={setTxPage}
              openTransactionId={openTransactionId}
            />
          </div>
        )}

        {outerTab === 'statements' && <StatementsTab />}

        {outerTab === 'slip_recon' && isAdminOrOwner && <SlipReconTab />}
      </Card>

      <UploadStatementModal isOpen={canUploadStatement && (uploadOpen || uploadRequested)} onClose={closeUpload} />

      <UploadStripePayoutModal
        isOpen={stripeUploadOpen}
        onClose={() => setStripeUploadOpen(false)}
      />
    </div>
  )
}
